import { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CognitoUserPool, CognitoUser } from 'amazon-cognito-identity-js'
import { cognitoConfig } from '@/lib/cognito-config'
import { useConfirm } from '@/hooks/useConfirm'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.userPoolId,
  ClientId: cognitoConfig.clientId,
})

export function VerifyEmailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { confirm, modal: confirmModal } = useConfirm()
  const email = location.state?.email || ''

  const [code, setCode] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.confirmRegistration(code, true, (err) => {
      setLoading(false)
      if (err) {
        setError(err.message)
        return
      }
      navigate('/login', { state: { message: t('verify.verifySuccess') } })
    })
  }

  const handleResend = () => {
    setResending(true)
    setError('')

    const cognitoUser = new CognitoUser({
      Username: email,
      Pool: userPool,
    })

    cognitoUser.resendConfirmationCode((err) => {
      setResending(false)
      if (err) {
        setError(err.message)
        return
      }
      confirm({
        title: t('common.success'),
        message: t('verify.resendSuccess'),
        confirmLabel: 'OK',
      })
    })
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100 p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-lg p-8">
        <div className="flex justify-end mb-4">
          <LanguageSwitcher />
        </div>

        <h1 className="text-2xl font-bold text-center text-gray-900 mb-2">
          {t('verify.title')}
        </h1>
        <p className="text-center text-gray-600 mb-6 text-sm">
          {t('verify.subtitle', { email }).split('{{email}}')[0]}
          <strong>{email}</strong>
          {t('verify.subtitle', { email }).split('{{email}}')[1]}
        </p>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded mb-4 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label htmlFor="code" className="block text-sm font-medium text-gray-700 mb-1">
              {t('verify.codeLabel')}
            </label>
            <input
              id="code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={t('verify.codePlaceholder')}
              className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
              maxLength={6}
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length !== 6}
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? t('verify.verifying') : t('verify.verifyButton')}
          </button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={handleResend}
            disabled={resending}
            className="text-sm text-blue-600 hover:text-blue-700 disabled:text-gray-400"
          >
            {resending ? t('verify.sending') : t('verify.resendButton')}
          </button>
        </div>

        <div className="mt-6 text-center">
          <a href="/login" className="text-sm text-gray-600 hover:text-gray-900">
            {t('verify.backToLogin')}
          </a>
        </div>
      </div>
      {confirmModal}
    </div>
  )
}
